import {
  Box,
  Chip,
  Paper,
  Drawer,
  styled,
  Avatar,
  Divider,
  Tooltip,
  useTheme,
  TextField,
  keyframes,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import DOMPurify from 'dompurify';
import { useChat } from './useChat';
import parseHtml from 'html-react-parser';
import { ChatDrawerProps } from './chat.types';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useIntl, FormattedMessage } from 'react-intl';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import React, { useState, useEffect, useRef } from 'react';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

// Bouncing dots animation keyframes for typing indicator
const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const Dot = styled('span')(({ theme }) => ({
  width: '6px',
  height: '6px',
  backgroundColor: theme.palette.mode === 'dark' ? '#fff' : '#666',
  borderRadius: '50%',
  display: 'inline-block',
  margin: '0 2px',
  animation: `${bounce} 1.2s infinite ease-in-out`,
  '&:nth-of-type(2)': {
    animationDelay: '0.2s',
  },
  '&:nth-of-type(3)': {
    animationDelay: '0.4s',
  },
}));

// Quick suggestions to help guide user interactions
const SUGGESTIONS_TR = [
  'Bu ay kaç saat çalıştım?',
  'Onay bekleyen efforlarım?',
  'Son 3 ay çalışma dağılımım?',
  'Bu ay en çok zaman harcadığım proje?',
];

const SUGGESTIONS_EN = [
  'How many hours did I work this month?',
  'My pending projects?',
  'My work schedule for the last 3 months?',
  'Which project did I spend the most time on this month?',
];

export const ChatDrawer = ({ open, onClose }: ChatDrawerProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { messages, loading, sendMessage, resetChat } = useChat();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isTurkish = intl.locale === 'tr';
  const suggestions = isTurkish ? SUGGESTIONS_TR : SUGGESTIONS_EN;

  // Scroll to bottom on new message or loading state change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open) {
      // Small timeout to allow the drawer slide animation to complete
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [messages, loading, open]);

  const handleSend = () => {
    if (!inputValue.trim() || loading) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (loading) return;
    sendMessage(suggestion);
  };

  // Basic markdown/formatting parser
  const formatMessageText = (text: string) => {
    if (!text) return '';

    // Safely encode basic HTML tags
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Bold: **text**
      .replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>')
      // Code blocks: ```code```
      .replace(
        /```([\s\S]*?)```/g,
        '<pre style="background: rgba(0,0,0,0.06); padding: 8px; border-radius: 6px; font-family: monospace; overflow-x: auto; margin: 6px 0; font-size: 0.85rem; border: 1px solid rgba(0,0,0,0.08);"><code>$1</code></pre>'
      )
      // Inline code: `code`
      .replace(
        /`([^`]+)`/g,
        '<code style="background: rgba(0,0,0,0.06); padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9rem;">$1</code>'
      )
      // Bullet points: lines starting with "- " or "* "
      .replace(/^\s*[-*]\s+(.+)$/gm, '<li style="margin-left: 12px; margin-bottom: 4px;">$1</li>')
      // Line breaks
      .replace(/\n/g, '<br />');

    // Clean up lists by wrapping list items in list tags if needed, or just let custom style render
    const cleanHtml = DOMPurify.sanitize(formatted, {
      ALLOWED_TAGS: ['strong', 'em', 'pre', 'code', 'li', 'br', 'span', 'p'],
      ALLOWED_ATTR: ['style'],
    });

    return parseHtml(cleanHtml);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(3px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400, md: 450 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: theme.shadows[24],
          backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#fcfcfd',
        },
      }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)'
              : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          color: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          zIndex: 1,
        }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            }}>
            <SmartToyIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ letterSpacing: '0.5px' }}>
              <FormattedMessage id="chat.assistantName" defaultMessage="Idecon AI Asistan" />
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#4ade80',
                  boxShadow: '0 0 8px #4ade80',
                }}
              />
              <FormattedMessage id="chat.online" defaultMessage="Aktif" />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title={intl.formatMessage({ id: 'chat.resetTooltip', defaultMessage: 'Sohbeti Temizle' })}>
            <IconButton
              onClick={resetChat}
              size="small"
              sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Message Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)',
            borderRadius: '10px',
            border: `2px solid ${theme.palette.mode === 'dark' ? '#121212' : '#fcfcfd'}`,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}>
        {messages.map((msg) => {
          const isUser = msg.sender === 'user';
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                width: '100%',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  maxWidth: '85%',
                  gap: 1,
                }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.85rem',
                    backgroundColor: isUser
                      ? theme.palette.secondary.main
                      : theme.palette.mode === 'dark'
                        ? '#312e81'
                        : '#e0e7ff',
                    color: isUser ? '#ffffff' : theme.palette.mode === 'dark' ? '#c7d2fe' : '#3730a3',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}>
                  {isUser ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                      backgroundColor: isUser
                        ? theme.palette.mode === 'dark'
                          ? '#4f46e5'
                          : '#6366f1'
                        : theme.palette.mode === 'dark'
                          ? '#1e1e24'
                          : '#f3f4f6',
                      color: isUser ? '#ffffff' : theme.palette.text.primary,
                      border: isUser
                        ? 'none'
                        : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                    }}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-line',
                        lineHeight: 1.5,
                        '& strong': { fontWeight: 600 },
                      }}>
                      {isUser ? msg.text : formatMessageText(msg.text)}
                    </Typography>
                  </Paper>
                  <Typography variant="caption" sx={{ mt: 0.5, px: 0.5, opacity: 0.5, fontSize: '0.7rem' }}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* AI Typing Indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: theme.palette.mode === 'dark' ? '#312e81' : '#e0e7ff',
                  color: theme.palette.mode === 'dark' ? '#c7d2fe' : '#3730a3',
                }}>
                <SmartToyIcon fontSize="small" />
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: '4px 16px 16px 16px',
                  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e24' : '#f3f4f6',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '40px',
                }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Dot />
                  <Dot />
                  <Dot />
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Suggestion Chips */}
      {messages.length <= 1 && !loading && (
        <Box sx={{ px: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ opacity: 0.6, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <QuestionAnswerIcon fontSize="inherit" />
            <FormattedMessage id="chat.suggestionsTitle" defaultMessage="Önerilen Sorular:" />
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {suggestions.map((suggestion, idx) => (
              <Chip
                key={idx}
                label={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                clickable
                variant="outlined"
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  py: 1.5,
                  borderRadius: '12px',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.06)',
                    borderColor: '#6366f1',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider />

      {/* Input Footer */}
      <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? '#151515' : '#f9fafb' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          placeholder={intl.formatMessage({ id: 'chat.placeholder', defaultMessage: 'Mesajınızı yazın...' })}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || loading}
                    sx={{
                      backgroundColor: inputValue.trim() ? '#6366f1' : 'transparent',
                      color: inputValue.trim() ? '#ffffff' : theme.palette.text.disabled,
                      '&:hover': {
                        backgroundColor: inputValue.trim() ? '#4f46e5' : 'transparent',
                      },
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                    }}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '18px',
                backgroundColor: theme.palette.mode === 'dark' ? '#222' : '#ffffff',
                pr: 1,
              },
            },
          }}
        />
      </Box>
    </Drawer>
  );
};

export default ChatDrawer;
